import React, { useEffect, useRef, useState } from 'react';
import { isEduEmail } from '../../utils/berkeleyEmailAccess';

/**
 * Read-only .edu address with EDIT / RESEND, matching Notification Preferences.
 * `variant="user-menu"` reuses those classes; `popup` uses the same look on cart popups.
 */
export default function PendingEduEmailField({
  email,
  cooldownSeconds = 0,
  busy = false,
  error = '',
  onSave,
  onResend,
  variant = 'popup',
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(() => email || '');
  const [localError, setLocalError] = useState('');
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const inputRef = useRef(null);
  const displayedEmail = (email || '').trim();

  useEffect(() => {
    if (!editing) setInput(displayedEmail);
  }, [displayedEmail, editing]);

  useEffect(() => {
    if (!editing) return undefined;
    const node = inputRef.current;
    if (!node) return undefined;
    node.focus();
    node.select();
    return undefined;
  }, [editing]);

  useEffect(() => {
    if (!resent) return undefined;
    const t = setTimeout(() => setResent(false), 4000);
    return () => clearTimeout(t);
  }, [resent]);

  const locked = busy || saving || resending;
  const canResend = cooldownSeconds <= 0 && !locked;
  const displayError = localError || error;

  const isUserMenu = variant === 'user-menu';
  const formClass = isUserMenu
    ? 'user-menu-notification-prefs-form'
    : 'pending-edu-email-form';
  const fieldClass = isUserMenu
    ? 'user-menu-notification-prefs-email-field'
    : 'pending-edu-email-field';
  const errorClass = isUserMenu ? 'user-menu-coupon-redeem-error' : 'berkeley-email-error';
  const editWrapClass = isUserMenu
    ? 'user-menu-notification-prefs-edit-wrap user-menu-edu-verify-actions'
    : 'pending-edu-email-actions';
  const pillClass = isUserMenu
    ? 'feed-tile-detail-btn feed-tile-detail-btn-refer user-menu-notification-prefs-edit-btn'
    : 'feed-tile-detail-btn feed-tile-detail-btn-refer pending-edu-email-btn';
  const saveClass = isUserMenu
    ? 'user-menu-notification-prefs-save-btn'
    : 'pending-edu-email-save-btn';
  const cancelClass = isUserMenu
    ? 'user-menu-notification-prefs-cancel-btn'
    : 'pending-edu-email-cancel-btn';
  const actionsClass = isUserMenu
    ? 'user-menu-notification-prefs-actions'
    : 'pending-edu-email-edit-actions';

  const startEdit = () => {
    if (locked) return;
    setInput(displayedEmail);
    setLocalError('');
    setResent(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    if (locked) return;
    setInput(displayedEmail);
    setLocalError('');
    setEditing(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (locked) return;
    const trimmed = String(input || '').trim().toLowerCase();
    if (!trimmed) {
      setLocalError('Please enter your .edu email.');
      return;
    }
    if (!isEduEmail(trimmed)) {
      setLocalError('Email must be a .edu address.');
      return;
    }
    if (trimmed === displayedEmail.toLowerCase()) {
      setLocalError('');
      setEditing(false);
      return;
    }
    if (typeof onSave !== 'function') return;
    setSaving(true);
    setLocalError('');
    try {
      const ok = await onSave(trimmed);
      if (!ok) {
        setLocalError('Could not save. Try again.');
      } else {
        setEditing(false);
      }
    } catch (err) {
      setLocalError(err?.message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || typeof onResend !== 'function') return;
    setResending(true);
    setLocalError('');
    setResent(false);
    try {
      const ok = await onResend();
      if (!ok) {
        setLocalError('Could not resend. Try again.');
      } else {
        setResent(true);
      }
    } catch (err) {
      setLocalError(err?.message || 'Could not resend. Try again.');
    } finally {
      setResending(false);
    }
  };

  if (editing) {
    return (
      <form className={formClass} onSubmit={handleSave}>
        <input
          ref={inputRef}
          type="email"
          className={fieldClass}
          aria-label="School email"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (localError) setLocalError('');
          }}
          autoComplete="email"
          disabled={locked}
        />
        {displayError ? <p className={errorClass}>{displayError}</p> : null}
        <div className={actionsClass}>
          <button type="submit" className={saveClass} disabled={locked}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className={cancelClass} disabled={locked} onClick={cancelEdit}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className={formClass}>
      <p className={fieldClass} aria-label="School email submitted for verification">
        {displayedEmail || '—'}
      </p>
      {displayError ? <p className={errorClass}>{displayError}</p> : null}
      {resent && !displayError ? (
        <p className={isUserMenu ? 'user-menu-edu-verify-resent' : 'pending-edu-email-resent'}>
          Link sent
        </p>
      ) : null}
      <div className={editWrapClass}>
        <button
          type="button"
          className={pillClass}
          aria-label="Edit school email"
          disabled={locked}
          onClick={startEdit}
        >
          EDIT
        </button>
        <button
          type="button"
          className={pillClass}
          aria-label="Resend school email verification"
          disabled={!canResend}
          onClick={handleResend}
        >
          {resending
            ? 'SENDING…'
            : cooldownSeconds > 0
              ? `RESEND ${cooldownSeconds}s`
              : 'RESEND'}
        </button>
      </div>
    </div>
  );
}
